// ═══════════════════════════════════════════════════════════
// Supabase Client — الاتصال بالداتابيز
// الدخول عبر Supabase Auth (JWT) — كل طلب يحمل جلسة المستخدم، وقاعدة البيانات
// تطبّق الصلاحيات حسب الدور (RLS). المفتاح العام وحده لا يقرأ أي بيانات (BUG-001/005).
// ═══════════════════════════════════════════════════════════

import { TABLES } from "./dbSchema";

const SUPABASE_URL = "https://zrywrkzoyakfvekassci.supabase.co";
const SUPABASE_KEY = "eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6InpyeXdya3pveWFrZnZla2Fzc2NpIiwicm9sZSI6ImFub24iLCJpYXQiOjE3OTA3NTk5MTMsImV4cCI6MjEwNjMzNTkxM30.svu3BLttrda8Tnspul_1ZoITrRFX3Gp27V5EirAybmQ";

const headers = {
  "apikey": SUPABASE_KEY,
  "Content-Type": "application/json",
};

const TABLE_LABELS = Object.fromEntries(Object.entries(TABLES).map(([k, v]) => [k, v.label]));
const NETWORK_ERROR = "تعذّر الاتصال بالخادم. تحقق من اتصال الإنترنت ثم حاول مرة أخرى.";

// ═══════════════════════════════════════════════════════════
// الجلسة (Supabase Auth)
// ═══════════════════════════════════════════════════════════
const SESSION_KEY = "cnr_session";
const readSession = () => { try { return JSON.parse(localStorage.getItem(SESSION_KEY)); } catch { return null; } };
let session = readSession();
const saveSession = (s) => {
  session = s ? { access_token: s.access_token, refresh_token: s.refresh_token, expires_at: s.expires_at || Math.floor(Date.now() / 1000) + (s.expires_in || 3600) } : null;
  if (session) localStorage.setItem(SESSION_KEY, JSON.stringify(session)); else localStorage.removeItem(SESSION_KEY);
};
export const hasSession = () => !!(session && session.access_token);

// انتهاء الجلسة (أو تعطيل الحساب) → App يعمل خروج
const expire = () => {
  saveSession(null);
  window.dispatchEvent(new CustomEvent("erp:session-expired"));
};

let refreshing = null;
const refreshSession = async () => {
  if (!session?.refresh_token) return false;
  if (!refreshing) {
    refreshing = (async () => {
      try {
        const res = await fetch(`${SUPABASE_URL}/auth/v1/token?grant_type=refresh_token`, {
          method: "POST", headers, body: JSON.stringify({ refresh_token: session.refresh_token }),
        });
        if (!res.ok) { expire(); return false; }
        saveSession(await res.json());
        return true;
      } catch { return false; } finally { setTimeout(() => { refreshing = null; }, 0); }
    })();
  }
  return refreshing;
};

const authHeader = async () => {
  if (session && session.expires_at - 60 < Date.now() / 1000) await refreshSession();
  return { Authorization: `Bearer ${session?.access_token || SUPABASE_KEY}` };
};

// fetch مع الجلسة؛ 401 = تجديد الجلسة مرة واحدة ثم المحاولة
const authFetch = async (url, options = {}) => {
  const run = async () => fetch(url, { ...options, headers: { ...headers, ...(options.headers || {}), ...(await authHeader()) } });
  let res;
  try { res = await run(); } catch { throw new Error(NETWORK_ERROR); }
  if (res.status === 401 && session) {
    if (await refreshSession()) {
      try { res = await run(); } catch { throw new Error(NETWORK_ERROR); }
    }
    if (res.status === 401) expire();
  }
  return res;
};

// ───── طلب موحّد: يحوّل أخطاء الشبكة وأخطاء PostgREST لرسائل مفهومة ─────
// أكواد الأخطاء الشائعة → رسائل عربية مفهومة؛ التفاصيل التقنية في الـ console فقط (BUG-063)
const PG_MESSAGES = {
  "23505": "القيمة مسجلة مسبقاً (تكرار)",
  "23503": "السجل مرتبط بسجلات أخرى ولا يمكن حذفه أو تعديله",
  "23514": "قيمة غير مقبولة لأحد الحقول",
  "23502": "حقل مطلوب فارغ",
  "22P02": "قيمة غير صالحة لأحد الحقول",
  "42501": "لا تملك صلاحية لهذه العملية",
  "PGRST301": "انتهت الجلسة، سجّل الدخول مرة أخرى",
};
const hasArabic = (s) => /[؀-ۿ]/.test(String(s || ""));
const describeError = async (res, op, table) => {
  let body = null;
  try { body = await res.json(); } catch { /* body مش JSON */ }
  const label = TABLE_LABELS[table] || table;
  console.warn(`[ERP] ${op} ${table}:`, res.status, body);
  if (res.status === 404) return new Error(`تعذّر ${op} (${label}): الجدول غير موجود في قاعدة البيانات`);
  // رسائل قواعد العمل من القاعدة (triggers) عربية أصلاً
  if (body && hasArabic(body.message)) return new Error(`تعذّر ${op} (${label}): ${body.message}`);
  if (res.status === 401 || res.status === 403) return new Error(`تعذّر ${op} (${label}): لا تملك صلاحية لهذه العملية`);
  const friendly = body && PG_MESSAGES[body.code];
  return new Error(`تعذّر ${op} (${label}): ${friendly || (body && body.message) || "خطأ في الخادم (" + res.status + ")"}`);
};

const request = async (url, options, op, table) => {
  const res = await authFetch(url, options);
  if (!res.ok) throw await describeError(res, op, table);
  if (res.status === 204) return [];
  const text = await res.text();
  return text ? JSON.parse(text) : [];
};

// ───── قراءة بيانات ─────
export const supaGet = (table, query = "") =>
  request(`${SUPABASE_URL}/rest/v1/${table}?${query}`, {}, "قراءة البيانات", table);

// ───── قراءة كل الصفوف على صفحات (BUG-020) ─────
// Supabase بيرجّع أول 1000 صف بس (max-rows) — هنا بنكمّل بالـ Range لحد ما نجيب العدد الكلي
const PAGE_SIZE = 1000;
export const supaGetAll = async (table, query = "") => {
  const out = [];
  let total = Infinity;
  while (out.length < total) {
    const res = await authFetch(`${SUPABASE_URL}/rest/v1/${table}?${query}`, {
      headers: { "Range-Unit": "items", "Range": `${out.length}-${out.length + PAGE_SIZE - 1}`, "Prefer": "count=exact" },
    });
    if (!res.ok && res.status !== 416) throw await describeError(res, "قراءة البيانات", table);
    const rows = res.status === 416 ? [] : await res.json();
    const m = (res.headers.get("content-range") || "").match(/\/(\d+)$/);
    total = m ? Number(m[1]) : out.length + rows.length;
    out.push(...rows);
    if (!rows.length) break;
  }
  return out;
};

// ───── إضافة بيانات ─────
// minimal: true = بدون إرجاع السجل — لازم لما المستخدم يضيف سجل مش مسموح له يقرأه
// (مثلاً تكليف لموظف آخر: كل واحد يقرأ إشعاراته فقط، و RLS يرفض الإرجاع)
export const supaInsert = (table, data, { minimal = false } = {}) =>
  request(`${SUPABASE_URL}/rest/v1/${table}`, {
    method: "POST", headers: { "Prefer": minimal ? "return=minimal" : "return=representation" },
    body: JSON.stringify(data),
  }, "الحفظ", table);

// ───── تحديث بيانات ─────
export const supaUpdate = async (table, query, data) => {
  const rows = await request(`${SUPABASE_URL}/rest/v1/${table}?${query}`, {
    method: "PATCH", headers: { "Prefer": "return=representation" },
    body: JSON.stringify(data),
  }, "التعديل", table);
  // PATCH بيرجع 200 حتى لو ما لقاش صفوف (أو RLS منعته) — نعتبره فشل
  if (Array.isArray(rows) && rows.length === 0) throw new Error(`تعذّر التعديل (${TABLE_LABELS[table] || table}): السجل غير موجود أو لا توجد صلاحية`);
  return rows;
};

// ───── حذف بيانات ─────
export const supaDelete = async (table, query) => {
  const rows = await request(`${SUPABASE_URL}/rest/v1/${table}?${query}`, {
    method: "DELETE", headers: { "Prefer": "return=representation" },
  }, "الحذف", table);
  if (Array.isArray(rows) && rows.length === 0) throw new Error(`تعذّر الحذف (${TABLE_LABELS[table] || table}): السجل غير موجود أو لا توجد صلاحية`);
  return rows;
};

// ───── دوال السيرفر (RPC) — أي { error } من الدالة يتحول لخطأ ─────
export const rpc = async (fn, args = {}) => {
  const data = await request(`${SUPABASE_URL}/rest/v1/rpc/${fn}`, { method: "POST", body: JSON.stringify(args) }, "تنفيذ العملية", fn);
  if (data && !Array.isArray(data) && data.error) throw new Error(data.error);
  return data;
};

// ═══════════════════════════════════════════════════════════
// الدخول والخروج وكلمات المرور
// ═══════════════════════════════════════════════════════════
// رسالة واحدة لا تكشف الإيميلات المسجلة (BUG-018)؛ الحد من المحاولات من Supabase Auth نفسه
export const loginUser = async (email, password) => {
  const normalized = String(email || "").trim().toLowerCase();
  let res;
  try {
    res = await fetch(`${SUPABASE_URL}/auth/v1/token?grant_type=password`, {
      method: "POST", headers, body: JSON.stringify({ email: normalized, password }),
    });
  } catch { throw new Error(NETWORK_ERROR); }
  if (res.status === 429) throw new Error("محاولات دخول كثيرة. حاول مرة أخرى بعد قليل");
  if (!res.ok) throw new Error("البريد الإلكتروني أو كلمة المرور غير صحيحة");
  saveSession(await res.json());
  const me = await getMe();
  if (!me) { await logoutUser(); throw new Error("البريد الإلكتروني أو كلمة المرور غير صحيحة"); }
  rpc("app_after_login").catch((err) => console.warn("login bookkeeping failed:", err.message));
  return me;
};

// بيانات المستخدم الحالي من الجلسة (الدور من القاعدة وليس من المتصفح — BUG-005/006/015)
export const getMe = async () => {
  if (!hasSession()) return null;
  const me = await rpc("app_me");
  return me && me.id ? me : null;
};

export const logoutUser = async () => {
  const token = session?.access_token;
  saveSession(null);
  if (!token) return;
  try {
    await fetch(`${SUPABASE_URL}/auth/v1/logout`, { method: "POST", headers: { ...headers, Authorization: `Bearer ${token}` } });
  } catch { /* الخروج المحلي تم */ }
};

// تغيير كلمة المرور: السيرفر يتحقق من الحالية ويطبق الحد الأدنى (BUG-017/018)
export const changePassword = (oldPassword, newPassword) =>
  rpc("app_change_password", { p_old: oldPassword, p_new: newPassword });

// قائمة الموظفين للتكليف والربط (id والاسم فقط) — بيانات المستخدمين الكاملة لـ exec/system/finance بس
export const getUsersDirectory = async () => (await rpc("app_users_directory")) || [];

// ───── إدارة المستخدمين — دوال السيرفر لـ system/exec فقط (BUG-016/019) ─────
export const getAllUsers = async () =>
  supaGet("users", "select=id,username,name,email,role,department,phone,is_active,last_login,created_at&order=id.asc");
export const adminCreateUser = ({ name, email, role, department, phone }) =>
  rpc("app_admin_create_user", { p_name: name, p_email: email, p_role: role, p_department: department || null, p_phone: phone || null });
export const adminResetPassword = (id) => rpc("app_admin_reset_password", { p_user_id: id });
export const adminUpdateUser = (id, { name = null, role = null, department = null, isActive = null } = {}) =>
  rpc("app_admin_update_user", { p_user_id: id, p_name: name, p_role: role, p_department: department, p_is_active: isActive });
export const adminDeleteUser = (id) => rpc("app_admin_delete_user", { p_user_id: id });

// ───── الإشعارات ─────
export const getNotifications = async (userId) =>
  supaGet("notifications", `user_id=eq.${userId}&order=created_at.desc&limit=10`);
export const markNotificationRead = async (id) =>
  supaUpdate("notifications", `id=eq.${id}`, { is_read: true });

// ═══════════════════════════════════════════════════════════
// المرفقات — bucket خاص + روابط موقّعة مؤقتة (BUG-009)
// ═══════════════════════════════════════════════════════════
const STORAGE_BUCKET = "attachments";
const cleanPathOf = (p) => String(p || "").replace(/^\/+/, "").split("/").map(encodeURIComponent).join("/");

export const uploadFile = async (filePath, file) => {
  const res = await authFetch(`${SUPABASE_URL}/storage/v1/object/${STORAGE_BUCKET}/${cleanPathOf(filePath)}`, {
    method: "POST",
    // بدون x-upsert: لا يمكن استبدال ملف موجود بنفس المسار
    headers: { "Content-Type": file.type || "application/octet-stream", "Cache-Control": "3600" },
    body: file,
  });
  if (!res.ok) {
    const text = await res.text().catch(() => "");
    console.warn("[ERP] upload failed:", res.status, text);
    throw new Error(res.status === 409 ? "يوجد ملف بنفس الاسم — أعد المحاولة" : res.status === 413 ? "حجم الملف أكبر من المسموح" : `تعذّر رفع الملف (${res.status})`);
  }
  return { path: String(filePath).replace(/^\/+/, ""), bucket: STORAGE_BUCKET };
};

// رابط موقّع صالح 10 دقائق — للمستخدم المسجّل فقط
export const getSignedUrl = async (filePath) => {
  if (!filePath) return "";
  const res = await authFetch(`${SUPABASE_URL}/storage/v1/object/sign/${STORAGE_BUCKET}/${cleanPathOf(filePath)}`, {
    method: "POST", body: JSON.stringify({ expiresIn: 600 }),
  });
  if (!res.ok) { console.warn("[ERP] sign url failed:", res.status); throw new Error("تعذّر فتح الملف"); }
  const { signedURL } = await res.json();
  return `${SUPABASE_URL}/storage/v1${signedURL}`;
};

// فتح مرفق في تبويب جديد — التبويب يُفتح فوراً (حتى لا يمنعه المتصفح) ثم يُوجَّه للرابط الموقّع
export const openFile = async (filePath) => {
  const w = window.open("about:blank", "_blank");
  try {
    const url = await getSignedUrl(filePath);
    if (w) { w.opener = null; w.location.href = url; } else window.location.assign(url);
  } catch (err) {
    if (w) w.close();
    window.dispatchEvent(new CustomEvent("erp:error", { detail: err.message }));
  }
};

export const deleteFile = async (filePath) => {
  const res = await authFetch(`${SUPABASE_URL}/storage/v1/object/${STORAGE_BUCKET}/${cleanPathOf(filePath)}`, { method: "DELETE" });
  if (!res.ok) {
    console.warn("[ERP] delete file failed:", res.status);
    throw new Error(`تعذّر حذف الملف (${res.status})`);
  }
  return true;
};

const api = {
  supaGet, supaGetAll, supaInsert, supaUpdate, supaDelete, rpc, loginUser, getMe, logoutUser, hasSession,
  changePassword, getUsersDirectory, getAllUsers, adminCreateUser, adminResetPassword, adminUpdateUser, adminDeleteUser,
  getNotifications, markNotificationRead, uploadFile, getSignedUrl, openFile, deleteFile,
};
export default api;
